import {
    Button,
    Card,
    Flex,
    Input,
    Statistic,
    Typography,
    notification,
} from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { useCallback, useEffect, useState } from "react";
import { getRequest, getRequestWithParams } from "../../../api/httpRequest";
import { docsStore } from "../../../store/admissions/docsStore";
import { DocData } from "../model/docData";
import { isContractSigningPeriodExpired } from "./contractSigningDeadline";

const { Text } = Typography;

interface ContractSigningFormProps {
    doc: DocData;
    /** Ожидание ввода кода абитуриентом — блокирует повтор «Отклонить» на карточке */
    applicantSmsPending: boolean;
    onApplicantSmsPendingChange: (value: boolean) => void;
}

const refreshContracts = async () => {
    const { setDocs } = docsStore.getState();
    try {
        const response = await getRequest("/server/proxy/contracts/getContracts");
        if (response.data?.length > 0) {
            setDocs(response.data);
        } else {
            setDocs([]);
        }
    } catch {
        /* список обновится при следующем заходе */
    }
};

export const ContractSigningForm = ({
    doc,
    applicantSmsPending,
    onApplicantSmsPendingChange,
}: ContractSigningFormProps) => {
    const { xl } = useBreakpoint();
    const isMobile = !xl;
    const [signedParentSms, setSignedParentSms] = useState(false);
    const [inputDisabled, setInputDisabled] = useState(doc.signed);
    const [inputParentDisabled, setInputParentDisabled] = useState(doc.signedSecondSign);
    const [applicantError, setApplicantError] = useState("");
    const [parentError, setParentError] = useState("");
    const [applicantOtpValue, setApplicantOtpValue] = useState("");
    const [parentOtpValue, setParentOtpValue] = useState("");
    const [api, contextHolder] = notification.useNotification();

    const signingExpired = isContractSigningPeriodExpired(doc);

    useEffect(() => {
        setSignedParentSms(false);
        onApplicantSmsPendingChange(false);
        setInputDisabled(doc.signed);
        setInputParentDisabled(doc.signedSecondSign);
        setApplicantOtpValue("");
        setParentOtpValue("");
        setApplicantError("");
        setParentError("");
    }, [doc.contractId, doc.signed, doc.signedSecondSign, onApplicantSmsPendingChange]);

    const handleSendSms = useCallback(
        (parent: boolean) => {
            if (!parent) setApplicantError("");
            else setParentError("");
            getRequestWithParams("/server/proxy/contracts/singDoc", {
                contractId: doc.contractId,
                parent,
            })
                .then((response) => {
                    if (response.data?.error) {
                        if (!parent) setApplicantError(response.data.error);
                        else setParentError(response.data.error);
                        return;
                    }
                    api.success({ message: "СМС успешно отправлена", duration: 2 });
                    if (!parent) {
                        onApplicantSmsPendingChange(true);
                    } else {
                        setSignedParentSms(true);
                    }
                    refreshContracts();
                })
                .catch((err) => {
                    const isServerError = !err?.response || err?.response?.status >= 500;
                    const msg = isServerError
                        ? "Ошибка подписи подкумента"
                        : err?.response?.data?.error || err?.message || "Не удалось отправить СМС";
                    if (!parent) setApplicantError(msg);
                    else setParentError(msg);
                    api.error({ message: "Не удалось отправить СМС", duration: 2 });
                });
        },
        [api, doc.contractId, onApplicantSmsPendingChange],
    );

    const handleSignDoc = useCallback(
        (parent: boolean, code: string) => {
            if (!parent) setApplicantError("");
            else setParentError("");
            getRequestWithParams("/server/proxy/contracts/verSig", {
                contractId: doc.contractId,
                parent,
                code,
            })
                .then((response) => {
                    if (response.data?.error) {
                        if (!parent) setApplicantError(response.data.error);
                        else setParentError(response.data.error);
                        if (!parent) {
                            onApplicantSmsPendingChange(false);
                        } else {
                            setSignedParentSms(false);
                        }
                        return;
                    }
                    api.success({ message: "Документ успешно подписан", duration: 2 });
                    if (!parent) {
                        setInputDisabled(true);
                    } else {
                        setInputParentDisabled(true);
                    }
                    refreshContracts();
                })
                .catch((err) => {
                    const isServerError = !err?.response || err?.response?.status >= 500;
                    const msg = isServerError
                        ? "Ошибка подписи подкумента"
                        : err?.response?.data?.error || err?.message || "Ошибка подписи подкумента";
                    if (!parent) setApplicantError(msg);
                    else setParentError(msg);
                    api.error({ message: msg, duration: 2 });
                    if (!parent) {
                        onApplicantSmsPendingChange(false);
                    } else {
                        setSignedParentSms(false);
                    }
                });
        },
        [api, doc.contractId, onApplicantSmsPendingChange],
    );

    const applicantBlock = (
        <Flex vertical gap={16} flex={1} style={{ minWidth: 0 }}>
            <Flex align="center" gap={8} wrap>
                <Text>Обучающийся:</Text>
                <Input.OTP
                    length={6}
                    value={applicantOtpValue}
                    disabled={inputDisabled || doc.signed || signingExpired}
                    onChange={(value) => {
                        setApplicantOtpValue(value);
                        setApplicantError("");
                        if (value.length === 6) {
                            handleSignDoc(false, value);
                        }
                    }}
                />
            </Flex>
            {applicantError && (
                <Flex align="center" gap={8}>
                    <Text type="danger" style={{ fontSize: 12 }}>{applicantError}</Text>
                    <Button size="small" onClick={() => { setApplicantOtpValue(""); setApplicantError(""); }}>
                        Сбросить
                    </Button>
                </Flex>
            )}
            <Flex align={isMobile ? "flex-start" : "center"} gap={16} vertical={isMobile}>
                <Button
                    type="primary"
                    disabled={applicantSmsPending || doc.signed || signingExpired}
                    onClick={() => handleSendSms(false)}
                >
                    Получить СМС код для подписания
                </Button>
                {applicantSmsPending && !inputDisabled && (
                    <Statistic.Timer
                        type="countdown"
                        value={Date.now() + 60 * 1000 * 5}
                        format="mm:ss"
                        onFinish={() => onApplicantSmsPendingChange(false)}
                    />
                )}
            </Flex>
        </Flex>
    );

    const payerBlock = doc.needSecondSign ? (
            <Flex
                flex={1}
                style={{
                    minWidth: 0,
                    borderTop: isMobile ? "1px solid #f0f0f0" : "none",
                    borderLeft: !isMobile ? "1px solid #f0f0f0" : "none",
                    paddingLeft: !isMobile ? 16 : 0,
                    paddingTop: isMobile ? 16 : 0,
                }}
                vertical
                gap={16}
            >
                <Flex align="center" gap={8} wrap>
                    <Text>Заказчик:</Text>
                    <Input.OTP
                        length={6}
                        value={parentOtpValue}
                        disabled={inputParentDisabled || doc.signedSecondSign || signingExpired}
                        onChange={(value) => {
                            setParentOtpValue(value);
                            setParentError("");
                            if (value.length === 6) {
                                handleSignDoc(true, value);
                            }
                        }}
                    />
                </Flex>
                {parentError && (
                    <Flex align="center" gap={8}>
                        <Text type="danger" style={{ fontSize: 12 }}>{parentError}</Text>
                        <Button size="small" onClick={() => { setParentOtpValue(""); setParentError(""); }}>
                            Сбросить
                        </Button>
                    </Flex>
                )}
                <Flex align="center" gap={16} wrap>
                    <Button
                        type="primary"
                        disabled={signedParentSms || doc.signedSecondSign || signingExpired}
                        onClick={() => handleSendSms(true)}
                    >
                        Получить СМС код для подписания
                    </Button>
                    {signedParentSms && !inputParentDisabled && !doc.signedSecondSign && (
                        <Statistic.Timer
                            type="countdown"
                            value={Date.now() + 60 * 1000 * 5}
                            format="mm:ss"
                            onFinish={() => setSignedParentSms(false)}
                        />
                    )}
                </Flex>
            </Flex>
        ) : null;

    return (
        <>
            {contextHolder}
            <Card className="contract-signing-form" size={isMobile ? "small" : "default"}>
                <Flex vertical gap={20}>
                    <Flex vertical={isMobile} gap={24} align={isMobile ? "stretch" : undefined} justify={isMobile ? undefined : doc.needSecondSign ? undefined : "flex-end"}>
                        {applicantBlock}
                        {payerBlock}
                    </Flex>
                </Flex>
            </Card>
        </>
    );
};
